import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Resizable } from "re-resizable";
import { forEach } from "lodash";
import Draggable from "react-draggable";
import { DiagramEngine, PortModelAlignment, PortWidget, DefaultPortModel } from "@projectstorm/react-diagrams";
import NENodeOperations from "../NENodeOperations";
import NENode from "../NENode";
import { NENodeModel } from "./NENodeModel";
import { NODE_CONSTANTS } from "../../../../utils/appConstants";
import { getPortName } from "../../../../utils/methods";
import { resetPortPositionGrid, resetPortPositionGridDoNotChangeHeight } from "../DraggableUtil";
import NENodeFooter from "../NENodeFooter";

export interface NENodeWidgetProps {
  engine: DiagramEngine;
  node: NENodeModel;
}
const NENodeWidget: React.FC<NENodeWidgetProps> = ({ engine, node }) => {
  const { portsList, groups, selectedNe, nodeDataCanvas } = node;
  const [grabbed, setGrabbed] = useState<boolean>(false);
  const [refreshPorts, setRefreshPort] = useState<boolean>(false);
  const [isNEMovementStopped, SetIsNEMovementStopped] = useState<boolean>(true);
  const onRefetch = React.useCallback(
    (event: any) => {
      engine.fireEvent(event, "onRefetchClick");
    },
    [engine]
  );
  const onDelete = React.useCallback(
    (event: any) => {
      engine.fireEvent(event, "onDeleteClick");
    },
    [engine]
  );
  const onGroupExpandCollapse = React.useCallback(
    (event: any) => {
      engine.fireEvent(event, "onGroupAction");
    },
    [engine]
  );
  const stopMoveNE = React.useCallback((event: any) => {
    if (event.button == 2) {
      event.stopPropagation();
    } else if (event.target) {
      const classStr = event.target.className || "";
      //console.log(event.target) // John!
      if (classStr) {
        // this css is the css in the network element root. WE stop the drag event from the divs with this css
        const illegalCSS = ["ports-root", "td-bg", "td-main", "no-ports", "group-container", "collapse", "port-in-group"];
        if (isNEMovementStopped) { illegalCSS.push('network-resources-node-root')}
        // console.log({classStr, illegalCSS})
        try {
          const found = classStr.split(" ").some((r: any) => illegalCSS.indexOf(r) >= 0);
          if (found) {
            event.stopPropagation();
          }
        } catch (e: any) {
          // catch ts error
        }
      } else {
        event.stopPropagation();
      }
    }
    // if (isNEMovementStopped) {event.stopPropagation()}
  }, [isNEMovementStopped]);

  const [width, setWidth] = useState<number>(nodeDataCanvas ? nodeDataCanvas.width : NODE_CONSTANTS.WIDTH.DEFAULT);
  const [height, setHeight] = useState<number>(nodeDataCanvas ? nodeDataCanvas.height : NODE_CONSTANTS.HEIGHT.DEFAULT);
  const { x: nodePositionX, y: nodePositionY } = node.getPosition();
  const [isMaximized, setIsMaximized] = useState(false);
   
  const setters = React.useMemo(() => {
    return {
      HEIGHT: setHeight,
      WIDTH: setWidth,
    };

  }, []);

  const resetPortPosition = useCallback(() => {
    resetPortPositionGridDoNotChangeHeight(node, height, width);

    const tempObject = { width, height };
    if (tempObject.width !== width || tempObject.height !== height) {
      handleChangeDimensions(tempObject.height - height, "HEIGHT");
      handleChangeDimensions(tempObject.width - width, "WIDTH");
    }
    setRefreshPort((prev) => !prev);
    engine.fireEvent({}, "onCanvasMove");
  }, [node, width, height]);

  const handleChangeDimensions = useCallback(
    (pixels: number, setterVal: "WIDTH" | "HEIGHT") => {
      const setter = setters[setterVal];
      if (pixels > 0) {
        const max = NODE_CONSTANTS[setterVal].MAX;
        setter((prev: number) => {
          const newValue = Math.min(max, prev + pixels);
          if (nodeDataCanvas) nodeDataCanvas[setterVal.toLowerCase()] = newValue;
          return newValue;
        });
      } else {
        const min = NODE_CONSTANTS[setterVal].MIN;
        setter((prev: number) => {
          const newValue = Math.max(min, prev + pixels);
          if (nodeDataCanvas) nodeDataCanvas[setterVal.toLowerCase()] = newValue; 
          return newValue;
        });
      }
      engine.fireEvent({}, "onCanvasMove");
    },
    [setters, nodeDataCanvas]
  );

  const onNodeMovingEnd = useCallback(() => {
    engine?.fireEvent({}, "onCanvasMove");
    setGrabbed(false);
  }, [engine]);

  const [canvasWidth, setCanvasWidth] = useState<number>(0);
  const [canvasHeight, setCanvasHeight] = useState<number>(0);
  
  useEffect(() => {
    const canvas = engine.getCanvas();
    if (canvas) {
      const boundingRectGrid = canvas.getBoundingClientRect();
      setCanvasWidth(boundingRectGrid.width);
      setCanvasHeight(boundingRectGrid.height);
    }
  }, [engine]);
  
  // Initial calculation (similar to old code) to avoid zero-based scaling
  useEffect(() => {
    const canvas = engine.getCanvas();
    if (canvas) {
      const boundingRectGrid = canvas.getBoundingClientRect();
      if (boundingRectGrid.width !== 0 && boundingRectGrid.height !== 0) {
        setCanvasWidth(boundingRectGrid.width);
        setCanvasHeight(boundingRectGrid.height);
      }
    }
  }, [engine]);
  
  const calculatedWidth = useMemo(() => Math.max(width, NODE_CONSTANTS.WIDTH.MIN), [width]);
  const calculatedHeight = useMemo(() => Math.max(height, NODE_CONSTANTS.HEIGHT.MIN), [height]);
  
  // Use canvasWidth and canvasHeight for further calculations

 return (
    <div className="network-resources-node-root" style={{ width: calculatedWidth, height: calculatedHeight }}>
      <div className={`operations ${grabbed ? "grabbed" : "grab"}`} onMouseDown={(e) => {
        if (isMaximized) {
          setGrabbed(true);
          stopMoveNE(e) // John: handle the movement here
        }
        else {
          setGrabbed(true);
        }
      }} onMouseUp={() => onNodeMovingEnd()}>
        <NENodeOperations
          engine={engine}
          groups={groups}
          onDeleteClick={onDelete}
          ports={portsList}
          selectedNe={selectedNe}
          setWidth={(pixels: number) => handleChangeDimensions(pixels, "WIDTH")}
          setHeight={(pixels: number) => handleChangeDimensions(pixels, "HEIGHT")}
          resetPortPosition={resetPortPosition}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          nodeWidth={calculatedWidth}
          nodeHeight={calculatedHeight}
          nodePositionX={nodePositionX}
          nodePositionY={nodePositionY}
          isMaximized={isMaximized}
          setIsMaximized={setIsMaximized}
        />
      </div>
      <div className="network-resources">
        <NENode
          engine={engine}
          node={node}
          onRefetch={onRefetch}
          onGroupExpandCollapse={onGroupExpandCollapse}
          nodeHeight={calculatedHeight}
          nodeWidth={calculatedWidth}
          refreshPorts={refreshPorts}
        />
      </div>
        <div className="network-footer" onMouseDown={(e) => {
          if (isMaximized) {
            stopMoveNE(e)
          }
          else {
            setGrabbed(true);
          }
          }}>
        <NENodeFooter
            selectedNe={selectedNe}
          />
        </div>
      </div>
  );
};

export default NENodeWidget;

